import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { PUBLIC_ACCOUNT_ACTIONS } from "models";
import "modules/AccessKeysDashboard/components/LogsModal/LogsModal.scss";
import { formatDate } from "utils/dateHelperFunctions";

import type { PublicAccountLog } from "models";

type LogsModalProps = {
  open: boolean;
  title: string;
  isFetching: boolean;
  logs?: PublicAccountLog[];
  onClose: () => void;
};

const columnHelper = createColumnHelper<PublicAccountLog>();

export const LogsModal = ({ title, logs, onClose, open, isFetching }: LogsModalProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "action-by",
        header: () => <span className="no-wrap-text">{t("v8_action_by", "Action by")}</span>,
        size: 120,
        cell: info => <span className="no-wrap-text">{info.row.original.created_by_name}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "action-at",
        header: () => <span className="no-wrap-text">{t("v8_action_at", "Action at")}</span>,
        size: 120,
        cell: info => <span className="no-wrap-text">{formatDate(info.row.original.created_on, DATE_FORMATS_FNS.dateMonthYear)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "action",
        header: () => <span className="no-wrap-text">{t("v8_action", "Action")}</span>,
        size: 120,
        cell: info => (
          <span className="no-wrap-text">
            {info.row.original.action === PUBLIC_ACCOUNT_ACTIONS.ShowKey ? t("v8_key_shown", "Key shown") : t("v8_key_revoked", "Key revoked")}
          </span>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "reason",
        header: () => <span className="no-wrap-text">{t("v8_reason", "Reason")}</span>,
        size: 120,
        cell: info => <span className="no-wrap-text">{info.row.original.reason}</span>
      })
    ],
    [t]
  );

  return (
    <Modal open={open} onClose={onClose} className="LogsModal">
      <Modal.Header className="logs-title">
        {t("v8_request_logs_of", "Request logs of")} {title}
        <div className="close-button-container">
          <Button className="modal-close-btn" basic onClick={onClose}>
            <Icon className="xmark grey" size="big" />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        <ReactTable isFetching={isFetching} columns={columns} data={logs} />
      </Modal.Content>
    </Modal>
  );
};
